import { useEffect, useState } from "react";
import { useHubSpotFields } from "../../hooks/useHubSpotFields";
import {
  fetchDescriptions,
  saveDescription,
  deleteDescription,
  fetchFieldValueTranslations,
  saveFieldValueTranslations,
  fetchFieldValueBadges,
  saveFieldValueBadges,
  type FieldValueTranslations,
} from "../../api/flows";
import { TRANSLATION_LOCALES, type TranslationLocale } from "../../types/configurator";
import { ChevronIcon } from "../ui/Icons";
import styles from "../FlowSelector/FlowSelector.module.css";

// The HubSpot product property whose options define the available badges.
// Reusing its option values keeps field-value badges styled and translated
// exactly like product badges in the widget.
const BADGE_FIELD = "featured_product";

const LOCALE_LABELS: Record<TranslationLocale, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  pt: "Português",
};

function countTranslatedLocales(t: FieldValueTranslations | undefined): number {
  if (!t) return 0;
  return TRANSLATION_LOCALES.reduce(
    (n, loc) => n + ((t[loc]?.description ?? "").trim() !== "" ? 1 : 0),
    0,
  );
}

export function FieldManagement() {
  const { fields, loading: fieldsLoading } = useHubSpotFields();
  const [selectedField, setSelectedField] = useState<string>("");
  const [descriptionMap, setDescriptionMap] = useState<Record<string, string>>({});
  const [translationsMap, setTranslationsMap] = useState<Record<string, FieldValueTranslations>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [activeLocale, setActiveLocale] = useState<Record<string, TranslationLocale>>({});
  const [badgeMap, setBadgeMap] = useState<Record<string, string[]>>({});

  useEffect(() => {
    fetchDescriptions().then(setDescriptionMap).catch(console.error);
    fetchFieldValueTranslations().then(setTranslationsMap).catch(console.error);
    fetchFieldValueBadges().then(setBadgeMap).catch(console.error);
  }, []);

  useEffect(() => {
    if (fields.length > 0 && !selectedField) {
      const sorted = [...fields].sort((a, b) => a.label.localeCompare(b.label));
      setSelectedField(sorted[0].name);
    }
  }, [fields, selectedField]);

  const handleBlur = async (field: string, value: string, text: string) => {
    const key = `${field}:${value}`;
    const trimmed = text.trim();
    setSaving((prev) => ({ ...prev, [key]: true }));
    try {
      if (trimmed) {
        await saveDescription(field, value, trimmed);
        setDescriptionMap((prev) => ({ ...prev, [key]: trimmed }));
      } else {
        await deleteDescription(field, value);
        setDescriptionMap((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleTranslationBlur = async (
    field: string,
    value: string,
    locale: TranslationLocale,
    text: string,
  ) => {
    const key = `${field}:${value}`;
    const trimmed = text.trim();
    const saveKey = `${key}:${locale}`;

    const current = translationsMap[key] ?? {};
    const next: FieldValueTranslations = { ...current };
    if (trimmed) {
      next[locale] = { ...(current[locale] ?? {}), description: trimmed };
    } else if (next[locale]) {
      const localeEntry = { ...next[locale]! };
      delete localeEntry.description;
      if (Object.keys(localeEntry).length === 0) delete next[locale];
      else next[locale] = localeEntry;
    }

    setSaving((prev) => ({ ...prev, [saveKey]: true }));
    try {
      await saveFieldValueTranslations(field, value, next);
      setTranslationsMap((prev) => {
        const out = { ...prev };
        if (Object.keys(next).length === 0) delete out[key];
        else out[key] = next;
        return out;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving((prev) => ({ ...prev, [saveKey]: false }));
    }
  };

  const toggleBadge = async (field: string, value: string, badge: string) => {
    const key = `${field}:${value}`;
    const current = badgeMap[key] ?? [];
    const next = current.includes(badge)
      ? current.filter((b) => b !== badge)
      : [...current, badge];

    const saveKey = `${key}:badges`;
    setSaving((prev) => ({ ...prev, [saveKey]: true }));
    try {
      await saveFieldValueBadges(field, value, next);
      setBadgeMap((prev) => {
        const out = { ...prev };
        if (next.length === 0) delete out[key];
        else out[key] = next;
        return out;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving((prev) => ({ ...prev, [saveKey]: false }));
    }
  };

  const badgeOptions = fields.find((f) => f.name === BADGE_FIELD)?.options ?? [];
  const currentField = fields.find((f) => f.name === selectedField);
  const sortedOptions = currentField
    ? [...currentField.options].sort((a, b) => a.label.localeCompare(b.label))
    : [];

  return (
    <div className={styles.screen}>
      <div className={styles.inner} style={{ maxWidth: 860 }}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Field Management</h1>
            <p className={styles.subtitle}>
              Set descriptions and badges for field values. These are shared across all flows.
            </p>
          </div>
          <button
            className="btn"
            onClick={() => { window.location.hash = ''; }}
          >
            ← Back to flows
          </button>
        </div>

        {fieldsLoading && (
          <div className={styles.grid}>
            {[0, 1, 2].map((i) => <div key={i} className={styles.skeleton} />)}
          </div>
        )}

        {!fieldsLoading && fields.length === 0 && (
          <p className={styles.empty}>No enumeration fields found in HubSpot.</p>
        )}

        {!fieldsLoading && fields.length > 0 && (
          <>
            <div style={{ marginBottom: 24 }}>
              <select
                value={selectedField}
                onChange={(e) => setSelectedField(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  fontFamily: 'var(--font)',
                  outline: 'none',
                  minWidth: 240,
                }}
              >
                {[...fields].sort((a, b) => a.label.localeCompare(b.label)).map((f) => (
                  <option key={f.name} value={f.name}>{f.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sortedOptions.map((opt) => {
                const key = `${selectedField}:${opt.value}`;
                const dutchDesc = descriptionMap[key] ?? '';
                const rowTranslations = translationsMap[key];
                const completeCount = countTranslatedLocales(rowTranslations);
                const allComplete = completeCount === TRANSLATION_LOCALES.length;
                const isOpen = expanded[key] ?? false;
                const loc = activeLocale[key] ?? 'en';

                return (
                  <div
                    key={opt.value}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr auto auto auto',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 16px',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                          {opt.label}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                          {opt.value}
                        </div>
                      </div>
                      <input
                        type="text"
                        placeholder="Add description…"
                        defaultValue={dutchDesc}
                        key={`${key}-${dutchDesc}`}
                        onBlur={(e) => handleBlur(selectedField, opt.value, e.target.value)}
                        style={{
                          padding: '7px 10px',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--bg-surface)',
                          color: 'var(--text-primary)',
                          fontSize: 13,
                          fontFamily: 'var(--font)',
                          outline: 'none',
                          width: '100%',
                        }}
                      />
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 40, textAlign: 'right' }}>
                        {saving[key] ? 'saving…' : dutchDesc ? '✓' : ''}
                      </span>
                      {completeCount > 0 && (
                        <span
                          title={`${completeCount} of ${TRANSLATION_LOCALES.length} locales translated`}
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid',
                            background: allComplete ? 'rgba(40, 140, 60, 0.12)' : 'rgba(140, 105, 55, 0.15)',
                            color: allComplete ? 'rgb(30, 110, 50)' : 'var(--warm-brown)',
                            borderColor: allComplete ? 'rgba(40, 140, 60, 0.35)' : 'rgba(140, 105, 55, 0.3)',
                          }}
                        >
                          🌐 {completeCount}/{TRANSLATION_LOCALES.length}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setExpanded((prev) => ({ ...prev, [key]: !isOpen }))}
                        title={isOpen ? 'Hide translations' : 'Show translations'}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          background: 'transparent',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                        }}
                      >
                        <ChevronIcon open={isOpen} />
                      </button>
                    </div>

                    {badgeOptions.length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 6,
                          padding: '0 16px 12px',
                        }}
                      >
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 4 }}>
                          Badges
                        </span>
                        {badgeOptions.map((b) => {
                          const active = (badgeMap[key] ?? []).includes(b.value);
                          return (
                            <button
                              key={b.value}
                              type="button"
                              aria-pressed={active}
                              disabled={saving[`${key}:badges`]}
                              onClick={() => toggleBadge(selectedField, opt.value, b.value)}
                              style={{
                                padding: '3px 10px',
                                background: active ? 'var(--warm-brown)' : 'transparent',
                                color: active ? '#fff' : 'var(--text-muted)',
                                border: '1px solid',
                                borderColor: active ? 'var(--warm-brown)' : 'var(--border)',
                                borderRadius: 999,
                                fontSize: 12,
                                fontFamily: 'var(--font)',
                                cursor: 'pointer',
                              }}
                            >
                              {active ? '✓ ' : ''}{b.label}
                            </button>
                          );
                        })}
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {saving[`${key}:badges`] ? 'saving…' : ''}
                        </span>
                      </div>
                    )}

                    {isOpen && (
                      <div
                        style={{
                          padding: '10px 16px 14px',
                          borderTop: '1px solid rgba(140, 105, 55, 0.15)',
                          background: 'var(--warm-brown-dim)',
                        }}
                      >
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                          {TRANSLATION_LOCALES.map((l) => {
                            const hasVal = (rowTranslations?.[l]?.description ?? '').trim() !== '';
                            const isActive = loc === l;
                            return (
                              <button
                                key={l}
                                type="button"
                                onClick={() => setActiveLocale((prev) => ({ ...prev, [key]: l }))}
                                style={{
                                  padding: '5px 10px',
                                  background: isActive ? 'var(--warm-brown)' : 'var(--bg-card)',
                                  color: isActive ? '#fff' : 'var(--text-primary)',
                                  border: '1px solid',
                                  borderColor: isActive
                                    ? 'var(--warm-brown)'
                                    : hasVal
                                      ? 'rgba(40, 140, 60, 0.45)'
                                      : 'var(--border)',
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: 12,
                                  fontFamily: 'var(--font)',
                                  cursor: 'pointer',
                                }}
                              >
                                {LOCALE_LABELS[l]}{hasVal ? ' ✓' : ''}
                              </button>
                            );
                          })}
                        </div>
                        <input
                          type="text"
                          placeholder={dutchDesc ? `NL: ${dutchDesc}` : '(no Dutch description set)'}
                          defaultValue={rowTranslations?.[loc]?.description ?? ''}
                          key={`${key}-${loc}-${rowTranslations?.[loc]?.description ?? ''}`}
                          onBlur={(e) => handleTranslationBlur(selectedField, opt.value, loc, e.target.value)}
                          style={{
                            padding: '7px 10px',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-sm)',
                            background: 'var(--bg-surface)',
                            color: 'var(--text-primary)',
                            fontSize: 13,
                            fontFamily: 'var(--font)',
                            outline: 'none',
                            width: '100%',
                          }}
                        />
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontStyle: 'italic' }}>
                            Empty translations fall back to the Dutch description.
                          </span>
                          <span>{saving[`${key}:${loc}`] ? 'saving…' : ''}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
